import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../utils/api';
import './Monitoring.css';

function Monitoring() {
  const [settings, setSettings] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState({});
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [settingsData, statusData] = await Promise.all([
        adminAPI.getMonitoringSettings(),
        adminAPI.getMonitoringStatus()
      ]);
      setSettings(settingsData);
      setStatus(statusData);
    } catch (err) {
      console.error('Failed to fetch monitoring data:', err);
      setError(err.response?.data?.detail || 'Failed to load monitoring settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggle = async (key, currentValue) => {
    setUpdating(prev => ({ ...prev, [key]: true }));
    try {
      const newSettings = await adminAPI.updateMonitoringSettings({ [key]: !currentValue });
      setSettings(newSettings);
      // Refresh status after toggle
      const newStatus = await adminAPI.getMonitoringStatus();
      setStatus(newStatus);
    } catch (err) {
      console.error(`Failed to toggle ${key}:`, err);
      setError(err.response?.data?.detail || `Failed to update ${key}`);
    } finally {
      setUpdating(prev => ({ ...prev, [key]: false }));
    }
  };

  if (loading) {
    return (
      <div className="monitoring">
        <div className="page-header">
          <h1 className="page-title">Monitoring</h1>
        </div>
        <div className="loading-container">
          <div className="spinner"></div>
          <span>Loading monitoring settings...</span>
        </div>
      </div>
    );
  }

  const settingItems = [
    {
      key: 'monitoring_enabled',
      label: 'Master Toggle',
      description: 'Enable or disable all monitoring features. When off, zero CPU/DB resources are consumed.',
      icon: PowerIcon,
      isPrimary: true
    },
    {
      key: 'prometheus_enabled',
      label: 'Prometheus Metrics',
      description: 'Collect HTTP request metrics, LLM query timing, and business metrics for Grafana dashboards.',
      icon: ChartIcon,
      disabled: !settings?.monitoring_enabled
    },
    {
      key: 'sentry_enabled',
      label: 'Sentry Error Tracking',
      description: 'Capture and track application errors with full stack traces and context.',
      icon: BugIcon,
      disabled: !settings?.monitoring_enabled
    },
    {
      key: 'alerting_enabled',
      label: 'Alerting (Slack/Email)',
      description: 'Send critical error notifications via Slack webhook and email.',
      icon: BellIcon,
      disabled: !settings?.monitoring_enabled
    }
  ];

  return (
    <div className="monitoring">
      <div className="page-header">
        <h1 className="page-title">Monitoring</h1>
        <p className="page-subtitle">
          Control monitoring features for the Quorum application. Disabling monitoring completely eliminates resource consumption.
        </p>
      </div>

      {error && (
        <div className="error-banner">
          <ErrorIcon className="error-icon" />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Status Overview */}
      {status && (
        <div className="status-grid">
          <div className={`status-card ${status.monitoring_active ? 'active' : ''}`}>
            <div className="status-indicator"></div>
            <div className="status-info">
              <span className="status-label">Monitoring</span>
              <span className="status-value">{status.monitoring_active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <div className={`status-card ${status.prometheus_active ? 'active' : ''}`}>
            <div className="status-indicator"></div>
            <div className="status-info">
              <span className="status-label">Prometheus</span>
              <span className="status-value">{status.prometheus_active ? 'Collecting' : 'Stopped'}</span>
            </div>
          </div>
          <div className={`status-card ${status.sentry_active ? 'active' : ''}`}>
            <div className="status-indicator"></div>
            <div className="status-info">
              <span className="status-label">Sentry</span>
              <span className="status-value">{status.sentry_active ? 'Tracking' : 'Disabled'}</span>
            </div>
          </div>
          <div className={`status-card ${status.alerting_active ? 'active' : ''}`}>
            <div className="status-indicator"></div>
            <div className="status-info">
              <span className="status-label">Alerting</span>
              <span className="status-value">{status.alerting_active ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>
      )}

      {/* Toggle Controls */}
      <div className="settings-section">
        <h2>Feature Toggles</h2>
        <div className="settings-list">
          {settingItems.map(({ key, label, description, icon: Icon, isPrimary, disabled }) => (
            <div
              key={key}
              className={`setting-item ${isPrimary ? 'primary' : ''} ${disabled ? 'disabled' : ''}`}
            >
              <div className="setting-icon">
                <Icon className="icon" />
              </div>
              <div className="setting-content">
                <div className="setting-header">
                  <span className="setting-label">{label}</span>
                  {disabled && <span className="disabled-badge">Requires Master Toggle</span>}
                </div>
                <p className="setting-description">{description}</p>
              </div>
              <div className="setting-toggle">
                <label className={`toggle-switch ${updating[key] ? 'updating' : ''}`}>
                  <input
                    type="checkbox"
                    checked={settings?.[key] || false}
                    onChange={() => handleToggle(key, settings?.[key])}
                    disabled={disabled || updating[key]}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Metrics Endpoint Info */}
      {settings?.prometheus_enabled && settings?.monitoring_enabled && (
        <div className="info-section">
          <h2>Prometheus Endpoint</h2>
          <div className="info-card">
            <p>Metrics are exposed at:</p>
            <code>/metrics</code>
            <p className="info-note">
              Configure your Prometheus scraper to collect metrics from this endpoint.
              Grafana dashboards can visualize HTTP latency, LLM query timing, and business metrics.
            </p>
          </div>
        </div>
      )}

      {/* Configuration Info */}
      <div className="info-section">
        <h2>Configuration</h2>
        <div className="config-grid">
          <div className="config-item">
            <span className="config-label">Sentry DSN</span>
            <span className={`config-value ${status?.sentry_configured ? 'configured' : 'not-configured'}`}>
              {status?.sentry_configured ? 'Configured' : 'Not configured'}
            </span>
          </div>
          <div className="config-item">
            <span className="config-label">Slack Webhook</span>
            <span className={`config-value ${status?.slack_configured ? 'configured' : 'not-configured'}`}>
              {status?.slack_configured ? 'Configured' : 'Not configured'}
            </span>
          </div>
          <div className="config-item">
            <span className="config-label">Alert Email</span>
            <span className={`config-value ${status?.email_configured ? 'configured' : 'not-configured'}`}>
              {status?.email_configured ? 'Configured' : 'Not configured'}
            </span>
          </div>
        </div>
        <p className="config-note">
          External services are configured via environment variables. See deployment documentation for details.
        </p>
      </div>
    </div>
  );
}

// Icons
function PowerIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
      <line x1="12" y1="2" x2="12" y2="12" />
    </svg>
  );
}

function ChartIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

function BugIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="8" y="6" width="8" height="14" rx="4" />
      <path d="M6 12H2" />
      <path d="M22 12h-4" />
      <path d="M6 6l-2-2" />
      <path d="M18 6l2-2" />
      <path d="M6 18l-2 2" />
      <path d="M18 18l2 2" />
    </svg>
  );
}

function BellIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function ErrorIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export default Monitoring;
